import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { withFormik, Field, Form, getIn } from 'formik';
import camelCaseRecursive from 'camelcase-keys-recursive';
import * as Yup from 'yup';
import { Button, Row, Col, Table } from 'react-bootstrap';
import {
  FormikInput,
  ButtonTooltip,
  DatatableEmployeeName,
  FormikDatePicker,
  FormikNumber,
  FormikSelect,
  Icon,
  SimpleCenteredModal,
  EmployeeSearchModal
} from '../../components';

import { indexSelectEmployeesRequest, indexEmployeesRequest, shiftEmployeeRequest } from '../../requests/employees';
import { selectorTypes, extraHoursTypes } from './FormOptions';
import { getMeRequest } from '../../requests/auth';
import { camelCaseEmptyStringRecursive } from '../../services/utils';
import { validateToken } from '../../actions/auth';

const basicOvertime = () => ({
  date: '',
  employeeId: '',
  hourValue: 'fifty',
  hours: 0,
  minutes: 0,
  overtimeType: '',
  bossSupervisorId: '',
  status: 'pending',
  directApprove: true,
  activities: '',
  shiftId: null
});

const basicUser = {
  avatar: [],
  email: '',
  id: '',
  name: '',
  first_name: '',
  nationalIdentification: '',
  role: '',
  employeeCard: {
    fileInfo: {}
  }
};

const OvertimesBossForm = props => {
  const { setFieldValue, setFieldTouched, values, isSubmitting } = props;
  const [selector, setSelector] = useState(selectorTypes);
  const [modalShow, setModalShow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState(basicUser);
  const [shiftsByEmployee, setShiftsByEmployee] = useState({});
  const [activityDate, setActivityDate] = useState(null);
  const [placeHolder, setPlaceHolder] = useState('Seleccionar Empleado');
  const dispatch = useDispatch();

  const dispatchGetMe = () => {
    getMeRequest({
      dispatch,
      successCallback: response => {
        if (response.data.logged_in) {
          setUser(camelCaseEmptyStringRecursive(response.data.user_info));
        } else {
          dispatch(validateToken());
        }
      }
    });
  };
  useEffect(dispatchGetMe, []);

  const handleSuccessIndex = response => {
    setIsLoading(false);
    setPlaceHolder('Seleccionar Empleado');
    const vEmployees = camelCaseRecursive(response.data.data);
    const vSelector = [...selectorTypes, ...vEmployees];
    setSelector(vSelector);
  };

  const fetchEmployees = () => {
    const employeeId = user.employeeCard?.id;
    if (!employeeId) return;

    setIsLoading(true);
    setPlaceHolder('Cargando Trabajadores...');
    indexSelectEmployeesRequest({
      dispatch,
      params: {
        employees_belong_boss: employeeId,
        active: true,
        is_dt: false,
        paginate: false,
        sort_column: 'name'
      },
      successCallback: handleSuccessIndex
    });
  };

  const fetchFullEmployees = employeeIds => {
    setIsLoading(true);
    return new Promise((resolve, reject) => {
      indexEmployeesRequest({
        dispatch,
        params: { active: true, is_dt: false, paginate: false, filter_id: employeeIds },
        successCallback: response => {
          const data = camelCaseRecursive(response.data.data);
          setIsLoading(false);
          resolve(data);
        },
        failureCallback: error => {
          reject(error);
        }
      });
    });
  };

  useEffect(fetchEmployees, [user]);

  // Setear bossSupervisorId en los valores del form cuando se obtiene el usuario
  useEffect(() => {
    const bossId = user.employeeCard?.id;
    if (bossId) {
      setFieldValue('bossSupervisorId', bossId);
    }
  }, [user, setFieldValue]);

  // Obtener turnos para un trabajador y fecha concreta
  const fetchShiftsFor = useCallback(
    (employeeId, date) => {
      if (!employeeId) return;
      shiftEmployeeRequest(employeeId, {
        dispatch,
        params: { paginate: false, replacementDate: date, serializer: 'basic_selector' },
        successCallback: response => {
          const data = response.data && response.data.data ? response.data.data : response.data;
          const shiftsArray = Array.isArray(data) ? data : [];
          setShiftsByEmployee(prev => ({ ...prev, [employeeId]: shiftsArray }));
        },
        failureCallback: () => {
          setShiftsByEmployee(prev => ({ ...prev, [employeeId]: [] }));
        }
      });
    },
    [dispatch]
  );

  const addAllEmployees = async () => {
    const allIds = selector.filter(selected => selected.id !== undefined).map(employee => employee.value);
    const employeesData = await fetchFullEmployees(allIds);
    setIsLoading(true);
    const { overtimes } = values;
    const employeesToAdd = employeesData.map(employee => ({
      ...basicOvertime(),
      employeeId: employee.value,
      employee
    }));
    const vOvertimes = [...overtimes, ...employeesToAdd];
    setSelector([]);
    setFieldValue('overtimes', vOvertimes);
    setIsLoading(false);
    // traer turnos para cada empleado agregado usando la fecha actual del formulario
    const date = values.date || null;
    employeesData.forEach(emp => fetchShiftsFor(emp.value, date));
  };

  // Cuando cambie la fecha global del formulario (campo date) o la lista de overtimes, actualizar turnos por trabajador
  useEffect(() => {
    const date = values.date || null;
    setActivityDate(date);
    if (!date) return;
    // obtener employeeIds presentes en overtimes
    const employeeIds = (values.overtimes || [])
      .map(o => o.employee && (o.employee.value || o.employee.id))
      .filter(Boolean);
    employeeIds.forEach(empId => {
      // sólo llamar si no tenemos ya datos para esa fecha
      fetchShiftsFor(empId, date);
    });
  }, [values.date, values.overtimes, fetchShiftsFor]);

  const handleSelector = async () => {
    const { employeeSelector, date } = props.values;
    const vSelector = selector.filter(selected => selected.value !== employeeSelector);
    setActivityDate(date);
    // setLocalEmployeeId(employeeSelector);

    if (vSelector.length === selector.length) {
      return;
    }
    if (employeeSelector === 'all_employees') {
      addAllEmployees();
    } else {
      const employeesData = await fetchFullEmployees(employeeSelector);
      const employeeToAdd = employeesData.find(selected => selected.value === employeeSelector);
      const { overtimes } = values;
      const overtimesLength = overtimes.length;
      setFieldValue(`overtimes[${overtimesLength}]`, {
        ...basicOvertime(),
        employeeId: employeeToAdd.value,
        employee: employeeToAdd
      });
      setSelector(vSelector);
      setFieldValue('employeeSelector', '');
      // traer turnos solamente para este empleado con la fecha actual
      const dateForm = values.date || null;
      fetchShiftsFor(employeeToAdd.value, dateForm);
    }
  };

  const removeFromTable = (employee, action) => {
    if (action === 'destroy') {
      const { overtimes } = values;
      const notRemovedEmployees = overtimes.filter(selected => selected.employee.id !== employee.id);
      const addToSelector = [...selector, employee];
      setSelector(addToSelector);
      setFieldValue('overtimes', notRemovedEmployees);
    }
  };

  const handleSearch = async selectedEmployees => {
    const vSelector = selector.filter(selected => !selectedEmployees.includes(selected.value));

    const employeesData = await fetchFullEmployees(selectedEmployees);

    setModalShow(false);
    if (vSelector.length === selector.length) return;
    const employeesToAdd = employeesData.map(employee => ({
      ...basicOvertime(),
      employeeId: employee.value,
      employee
    }));
    setFieldValue('overtimes', [...values.overtimes, ...employeesToAdd]);
    setSelector(vSelector);
  };

  const { onHide, submitVariant, errors, touched } = props;
  const { overtimes, employeeSelector } = values;

  return (
    <Form>
      <Field type="hidden" name="bossSupervisorId" />
      <div className="info-box ml-0">
        <p className="info">
          <span className="full-width">Seleccione trabajadores para incluir en el proceso</span>
        </p>
      </div>
      <Row className="align-items-center mb-3 mb-md-0">
        <Col md={2}>
          <Field name="date">
            {({ field }) => (
              <FormikDatePicker
                {...field}
                abbr
                label="Fecha de la Actividad"
                isOutsideRange={() => false}
                placeholder="dd/mm/aaaa"
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col md={5}>
          <Field name="employeeSelector">
            {({ field }) => (
              <FormikSelect
                {...field}
                abbr
                label="Generar Para"
                isDisabled={isLoading}
                isLoading={isLoading}
                placeholder={placeHolder}
                options={selector}
                onChange={data => setFieldValue(field.name, data ? data.value : '')}
                setFieldTouched={() => setFieldTouched(field.name)}
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col md={3}>
          <Field name="activities">
            {({ field }) => (
              <FormikInput
                {...field}
                abbr
                label="Actividad"
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col xs={2} md={2} xl={2}>
          <Button
            disabled={isLoading || employeeSelector.length === 0 || activityDate === null}
            variant="primary"
            onClick={handleSelector}
            style={{ marginTop: '12px' }}
          >
            Agregar
          </Button>
        </Col>
      </Row>
      <Row>{isLoading && <div className="loader">Procesando...</div>}</Row>
      {overtimes.length > 0 && (
        <Row>
          <Table responsive style={{ fontSize: '10px' }}>
            <thead>
              <tr>
                <th style={{ width: '250px' }}>NOMBRE DEL TRABAJADOR</th>
                <th className="text-center" style={{ width: '170px' }}>
                  TURNO
                </th>
                <th className="text-center" style={{ width: '150px' }}>
                  TIPO DE HORA
                </th>
                <th className="text-center" style={{ width: '120px' }}>
                  HORAS
                </th>
                <th className="text-center" style={{ width: '120px' }}>
                  MINUTOS
                </th>
                <th className="text-center" style={{ width: '120px' }}>
                  ACCIONES
                </th>
              </tr>
            </thead>
            <tbody>
              {overtimes.map((overtime, index) => {
                return (
                  <tr key={`overtime-${index.toString()}`}>
                    <td className="employee-name">
                      <DatatableEmployeeName
                        item={overtime.employee}
                        fileName="fileInfo"
                        name="fullName"
                        fileUrl="fileUrl"
                      />
                    </td>
                    <td>
                      <Field name={`overtimes[${index}][shiftId]`}>
                        {({ field }) => (
                          <FormikSelect
                            {...field}
                            abbr
                            placeholder="Seleccionar Turno"
                            options={
                              shiftsByEmployee[
                                overtime.employee && (overtime.employee.value || overtime.employee.id)
                              ] || []
                            }
                            onChange={data => setFieldValue(field.name, data ? data.value : '')}
                            setFieldTouched={() => setFieldTouched(field.name)}
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </td>
                    <td>
                      <Field name={`overtimes[${index}][overtimeType]`}>
                        {({ field }) => (
                          <FormikSelect
                            {...field}
                            abbr
                            placeholder="Seleccionar"
                            options={extraHoursTypes}
                            defaultValue={overtime.overtimeType}
                            onChange={data => setFieldValue(field.name, data ? data.value : '')}
                            setFieldTouched={() => setFieldTouched(field.name)}
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </td>
                    <td>
                      <Field name={`overtimes[${index}][parsedHours]`}>
                        {({ field }) => (
                          <FormikNumber
                            {...field}
                            rightAddon="hrs"
                            placeholder="1"
                            fieldName={`overtimes[${index}][hours]`}
                            value={overtime.hours}
                            setFieldValue={setFieldValue}
                            errors={errors}
                            touched={touched}
                            margin="mb-0"
                          />
                        )}
                      </Field>
                    </td>
                    <td>
                      <Field name={`overtimes[${index}][parsedMinutes]`}>
                        {({ field }) => (
                          <FormikNumber
                            {...field}
                            rightAddon="min"
                            placeholder="0"
                            fieldName={`overtimes[${index}][minutes]`}
                            value={overtime.minutes}
                            setFieldValue={setFieldValue}
                            errors={errors}
                            touched={touched}
                            margin="mb-0"
                          />
                        )}
                      </Field>
                    </td>
                    <td>
                      <ButtonTooltip
                        onClick={() => removeFromTable(overtime.employee, 'destroy')}
                        variant="circle-danger"
                        className="btn-circle"
                        size="sm"
                        text="Eliminar"
                      >
                        <Icon icon="trash" />
                      </ButtonTooltip>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Row>
      )}
      {overtimes.length > 0 && (
        <Row className="d-flex justify-content-end m-t-10 m-b-30">
          <Col md={2} className="mb-3">
            <Button type="submit" disabled={isSubmitting} variant={submitVariant} block onClick={onHide}>
              Finalizar
            </Button>
          </Col>
        </Row>
      )}
      <SimpleCenteredModal
        title="Buscar Empleados"
        body={
          <EmployeeSearchModal
            customParams={{ active: true }}
            handleClose={() => setModalShow(false)}
            formRequest={handleSearch}
          />
        }
        show={modalShow}
        onHide={() => setModalShow(false)}
      />
    </Form>
  );
};

const setInitialValues = () => {
  return {
    overtimes: [],
    employeeSelector: '',
    activities: ''
  };
};

const validationSchema = Yup.object().shape({
  overtimes: Yup.array().of(
    Yup.object().shape(
      {
        hours: Yup.number().when('minutes', {
          is: val => val === 0,
          then: Yup.number()
            .required('Debes ingresar la cantidad de horas')
            .integer('Debe ser un número entero')
            .min(1, 'Debe ser mayor o igual a 1')
            .max(12, 'Debe ser menor o igual a 12 horas'),
          otherwise: Yup.number().nullable()
        }),
        minutes: Yup.number().when('hours', {
          is: val => val === 0,
          then: Yup.number()
            .required('Debes ingresar la cantidad de minutos')
            .integer('Debe ser un número entero')
            .min(1, 'Debe ser mayor o igual a 1')
            .max(59, 'Debe ser menor o igual a 60 minutos'),
          otherwise: Yup.number().nullable()
        }),
        overtimeType: Yup.string().required('Debes seleccionar un tipo de hora extra')
      },
      [['hours', 'minutes']]
    )
  ),
  date: Yup.string().required('Debes seleccionar una fecha'),
  activities: Yup.string()
    .required('Debes ingresar una actividad')
    .min(5, 'Debe ser mayor a 5 caracteres')
    .max(50, 'Debe ser menor a 50 caracteres')
});

const handleSubmit = (values, { props, setSubmitting, resetForm, setFieldValue }) => {
  const { formRequest } = props;
  const { overtimes, activities, date, bossSupervisorId } = values;

  const cleanOvertimesData = {
    overtimes: overtimes.map(({ employee, parsedHours, parsedMinutes, ...rest }) => ({
      ...rest,
      activities,
      date,
      bossSupervisorId
    }))
  };

  formRequest(cleanOvertimesData, setSubmitting, resetForm, setFieldValue);
};

export default withFormik({
  mapPropsToValues: props => setInitialValues(props),
  validationSchema,
  handleSubmit,
  enableReinitialize: true,
  validateOnMount: props => props.action !== 'new'
})(OvertimesBossForm);
